import React from 'react';
import { I18n } from 'react-redux-i18n';
import { FormatFieldSelector } from '@common/components/format-field-selector';
import { MODULE_NAME } from '@modules/invite-status/constants/shared';
import { FolderPrimaryListDto } from '@common/dto/folder/primary-list-dto';
import { FormatFieldItem } from '@common/components/format-field-selector/format-field-selector.types';
import { HttpErrorDto } from '@common/dto/http';
import { Flex } from '@common/components/Flex';
import { Button } from '@common/components/Button';

import './query-chooser.styles.scss';

export type QueryChooserProps = {
  showErrorMessage: (message: string) => void;
  getFolders: () => Promise<Array<FolderPrimaryListDto>>;
  getFormats: (params: { searchKeyword?: string; folder?: FolderPrimaryListDto }) => Promise<FormatFieldItem[]>;
  selectedFormat?: FormatFieldItem;
  onSelectedFormatChange: (format: FormatFieldItem) => void;
  onNewFormat: () => void;
};

export const QueryChooser: React.FC<QueryChooserProps> = (props) => { 
  const { getFolders, getFormats, selectedFormat, showErrorMessage, onSelectedFormatChange, onNewFormat } = props;
  const [selectedFolder, setSelectedFolder] = React.useState<FolderPrimaryListDto | undefined>();
  const [folders, setFolders] = React.useState<Array<FolderPrimaryListDto>>([]);
  const [formats, setFormats] = React.useState<Array<FormatFieldItem>>([]);
  const [isFoldersLoading, setIsFoldersLoading] = React.useState<boolean>(false);
  const [isFormatsLoading, setIsFormatsLoading] = React.useState<boolean>(false);
  const [searchKeyword, setSearchKeyword] = React.useState<string>('');

  const getAndSetFolders = React.useCallback(async () => {
    setIsFoldersLoading(true);
    try {
      const folders = await getFolders();
      setFolders(folders);
    } catch (err) {
      console.error(err);
      showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to load folders'));
    } finally {
      setIsFoldersLoading(false);
    }
  }, [getFolders, showErrorMessage]);

  React.useEffect(() => {
    getAndSetFolders();
  }, []);

  const getAndSetFormats = React.useCallback(async (params: { folder?: FolderPrimaryListDto; searchKeyword?: string }) => {
    setIsFormatsLoading(true);
    try {
      const formats = await getFormats(params);
      setFormats(formats);
    } catch (err) {
      console.error(err);
      showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to load formats'));
    } finally {
      setIsFormatsLoading(false);
    }
  }, [getFormats, showErrorMessage]);

  const handleSelectedFolderChange = React.useCallback((folder: FolderPrimaryListDto) => {
    setSelectedFolder(folder);
    getAndSetFormats({ folder });
  }, [getAndSetFormats]);

  const handleSearchChange = React.useCallback((searchKeyword: string) => {
    setSearchKeyword(searchKeyword);
    getAndSetFormats({ searchKeyword });
  }, [getAndSetFormats, selectedFolder]);

  return (
    <div className="InviteStatusQueryChooser">
      <Flex justify="space-between" alignItems="center">
        <h6 className="InviteStatusQueryChooser__title">{I18n.t('selectExistOrNewQuery')}</h6>
        <Button onClick={onNewFormat}>{I18n.t('New Format')}</Button>
      </Flex>
      <FormatFieldSelector
        moduleName={MODULE_NAME}
        withSearch
        folders={folders}
        isFoldersLoading={isFoldersLoading}
        onSelectedFolderChange={handleSelectedFolderChange}
        formats={formats}
        isFormatsLoading={isFormatsLoading}
        onSelectedFormatChange={onSelectedFormatChange}
        searchKeyword={searchKeyword}
        onSearchKeyword={handleSearchChange}
        selectedFolder={selectedFolder}
        selectedFormat={selectedFormat}
        className="InviteStatusQueryChooser__format-selector"

        // TODO check ids

        // getId={{
        //   searchIcon: getId?.searchIcon,
        //   search: getId?.search,
        //   folder: getId?.folder,
        // }}
      />
    </div>
  );
};